import { fake } from '../test-support/fake';
import {
  AvFileProcess,
  Breadcrumb,
  DataRoomCategory,
  PiiFileProcess,
  ProblematicFiles,
  ProblematicFilesObject,
} from './generated';
import {
  FileProcessStatus,
  GenericFileProcess,
} from './generated';
import {
  AppConfig,
  CedantDetails,
  CleanupReport,
  CleanupStatus,
  Directory,
  ErrorMessage,
  Event,
  EventsResponse,
  EventType,
  File,
  FileProcessError,
  FileProcessErrorCode,
  FileProcessErrors,
  FileStatus,
  FileTypeCheckStatus,
  PiiApproval,
  PiiDetails,
  PiiInfo,
  PiiInfoType,
  PiiMatch,
  PiiStatus,
  Role,
  StorageObject,
  StorageObjects,
  Transformation,
  TransformationErrorCode,
  TransformationErrors,
  TreatyUser,
  TreatyUsers,
  UploadSignedUrl,
  User,
  UserType,
  ValidatedFilename,
  ValidatedFilenameError,
  ValidatedFilenameErrorCode,
  ValidatedFilenameWarning,
  ValidatedFilenameWarningCode,
  VirusStatus,
} from './generated';

const recentISODate = (): string => new Date().toISOString();

export const makeMockList = <T>(
  factory: (x: T, i: number) => T,
  size: number
): T[] => new Array(size).fill(null).map(factory);

// https://stackoverflow.com/a/57621892
export const oneOfEnum = <E extends Record<string | number, string | number>>(en: E): E[keyof E] => {
  const enumValues = Object.values(en) as E[keyof E][];
  const randomIndex = Math.floor(Math.random() * enumValues.length);
  return enumValues[randomIndex];
};

export const makeMockUser = (props?: Partial<User>): User => ({
  id: fake.uuid(),
  name: fake.fullName(),
  email: fake.email(),
  machineUser: false,
  ...props,
  __typename: 'User',
});

export const makeMockErrorMessage = (
  props?: Partial<ErrorMessage>
): ErrorMessage => ({
  column: fake.accountName(),
  errType: fake.sentence(),
  raw: fake.sentence(),
  row: fake.number({ min: 1 }),
  value: fake.number({ min: 1 }).toString(),
  ...props,
  __typename: 'ErrorMessage',
});

export const makeMockTransformationErrors = (
  props?: Partial<TransformationErrors>
): TransformationErrors => ({
  code: oneOfEnum(TransformationErrorCode),
  messages: makeMockList(() => makeMockErrorMessage(), 5),
  ...props,
  __typename: 'TransformationErrors',
});

export const makeMockTransformation = (
  props?: Partial<Transformation>
): Transformation => ({
  transformedAt: recentISODate(),
  errors: makeMockTransformationErrors(),
  ...props,
  __typename: 'Transformation',
});

export const makeMockPiiInfo = (props?: Partial<PiiInfo>): PiiInfo => ({
  type: PiiInfoType.PASSPORT,
  label: 'Passport number',
  ...props,
  __typename: 'PIIInfo',
});

export const makeMockPiiMatch = (props?: Partial<PiiMatch>): PiiMatch => ({
  infoType: makeMockPiiInfo(),
  count: 1,
  ...props,
  __typename: 'PIIMatch',
});

export const makeMockApproval = (
  props?: Partial<PiiApproval>
): PiiApproval => ({
  approvedAt: fake.pastDate().toISOString(),
  approver: makeMockUser(),
  comment: fake.sentence(),
  ...props,
  __typename: 'PIIApproval',
});

export const makeMockAppConfig = (props?: Partial<AppConfig>): AppConfig => ({
  canDownloadPiiReport: true,
  canManageUsers: true,
  canViewProblemFiles: true,
  version: '1',
  ...props,
  __typename: 'AppConfig',
});

export const makeMockPiiDetails = (
  props?: Partial<PiiDetails>
): PiiDetails => ({
  approvals: makeMockList(() => makeMockApproval(), 1),
  matches: makeMockList(() => makeMockPiiMatch(), 1),
  ...props,
  __typename: 'PIIDetails',
});

export const makeMockFileProcessError = (
  props?: Partial<FileProcessError>
): FileProcessError => ({
  code: oneOfEnum(FileProcessErrorCode),
  message: fake.sentence(),
  ...props,
  __typename: 'FileProcessError',
});

export const makeMockFileProcessErrors = (
  lengths: { count: number; totalCount: number },
  props?: Partial<FileProcessErrors>
): FileProcessErrors => {
  return {
    errors: makeMockList(() => makeMockFileProcessError(), lengths.count),
    count: lengths.totalCount,
    ...props,
    __typename: 'FileProcessErrors',
  };
};

export const makeMockGenericFileProcess = (
  props?: Partial<GenericFileProcess>
): GenericFileProcess => ({
  name: `File Process ${fake.number()}`,
  status: oneOfEnum(FileProcessStatus),
  errors: null,
  ...props,
  __typename: 'GenericFileProcess',
});

export const makeMockPiiFileProcess = (
  props?: Partial<PiiFileProcess>
): PiiFileProcess => ({
  name: 'PII Scan',
  piiStatus: oneOfEnum(PiiStatus),
  errors: null,
  ...props,
  __typename: 'PIIFileProcess',
});

export const makeMockAvFileProcess = (
  props?: Partial<AvFileProcess>
): AvFileProcess => ({
  name: 'AntiVirus Scan',
  virusStatus: oneOfEnum(VirusStatus),
  errors: null,
  ...props,
  __typename: 'AVFileProcess',
});

export const makeMockFile = (props?: Partial<File>): File => {
  const name = props?.name || fake.fileName();
  const versions = props?.versions || [];
  const parentDirectoryId = props?.parentDirectoryId || `id-parent-of-${name}`;
  let path = fake.filePath().slice(1); // trim leading slash
  path = path.slice(0, path.lastIndexOf('/') + 1); // remove filename
  return {
    id: `id-${name}`,
    cedant: 'SYMETRA',
    contentType: 'application/text',
    path,
    parentDirectoryId,
    size: fake.number(),
    createdAt: recentISODate(),
    updatedAt: recentISODate(),
    status: FileStatus.PROCESSED,
    virusStatus: VirusStatus.CLEAN,
    piiStatus: PiiStatus.APPROVED,
    version: recentISODate(),
    createdBy: makeMockUser(),
    updatedBy: makeMockUser(),
    versions: versions,
    transformation: makeMockTransformation(),
    piiDetails: makeMockPiiDetails(),
    processes: [
      makeMockGenericFileProcess(),
      makeMockPiiFileProcess(),
      makeMockAvFileProcess(),
    ],

    breadcrumbs: [
      makeMockBreadcrumb({ name: 'Symetra', directoryId: null }), // treaty
      makeMockBreadcrumb({ directoryId: parentDirectoryId }),
      makeMockBreadcrumb({ name, directoryId: null }), // current file
    ],
    canRead: true,
    canMove: true,
    canArchive: false,
    canDelete: false,
    canDownload: false,
    canPiiJustify: false,
    canPiiApprove: false,
    canRename: false,
    canRetryProcesses: false,
    automatedUpload: false,
    fileTypeCheckStatus: FileTypeCheckStatus.SUPPORTED,
    ...props,
    name,
    __typename: 'File',
  };
};

export const makeMockFileWithVersions = (props?: Partial<File>): File => {
  const mockFile = makeMockFile(props);
  const versions = makeMockList<File>(
    (_, i) =>
      makeMockFile({
        name: props?.name + `v${i}`,
        id: fake.uuid(),
        createdAt: `2021-03-0${i + 1}T11:31:57Z`,
        updatedAt: `2021-03-0${i + 1}T11:31:57Z`,
      }),
    5
  );
  return {
    ...mockFile,
    versions: [mockFile, ...versions],
  };
};

export const makeMockBreadcrumb = (props?: Partial<Breadcrumb>): Breadcrumb => {
  return {
    name: fake.noun(),
    directoryId: fake.uuid(),
    ...props,
    __typename: 'Breadcrumb',
  };
};

export const makeMockDirectory = (props?: Partial<Directory>): Directory => {
  const name = props?.name || fake.words();
  const parentDirectoryId = props?.parentDirectoryId || `id-parent-of-${name}`;

  return {
    id: `id-${name}`,
    name,
    cedant: 'SYMETRA',
    path: fake.directoryPath(),
    parentDirectoryId,
    fileCount: fake.number(10),
    directoryCount: fake.number(10),
    canArchive: true,
    breadcrumbs: [
      makeMockBreadcrumb({ name: 'Symetra', directoryId: null }), // treaty
      makeMockBreadcrumb({ directoryId: parentDirectoryId }),
      makeMockBreadcrumb({ name }), // current directory
    ],
    // Keep the objects empty - only overwrite the values during mock usage on tests, gql handlers etc.
    objects: {
      objects: [],
      count: 0,
      __typename: 'StorageObjects',
    },
    ...props,
    __typename: 'Directory',
  };
};

export const makeMockStorageObjects = (
  objects?: Array<StorageObject>
): StorageObjects => {
  const mockFiles = makeMockList(makeMockFile, 10);
  const mockDirectories = makeMockList(makeMockDirectory, 2);
  const storageObjects =
    objects || ([...mockDirectories, ...mockFiles] as Array<StorageObject>);

  return {
    objects: storageObjects,
    count: storageObjects.length,
    __typename: 'StorageObjects',
  };
};

export const makeMockUploadSignedURL = (
  props?: Partial<UploadSignedUrl>
): UploadSignedUrl => ({
  signedURL: fake.url() + '/file-upload',
  fileID: fake.uuid(),
  fileVersionID: fake.uuid(),
  headers: [],
  ...props,
  __typename: 'UploadSignedURL',
  parentDirectoryID: fake.uuid(),
});



export const makeMockValidatedFilename = (
  props?: Partial<ValidatedFilename>
): ValidatedFilename => ({
  id: fake.uuid(),
  filepath: fake.filePath(),
  isValid: true,
  ...props,
  __typename: 'ValidatedFilename',
});

export const makeMockValidatedFilenameError = (
  props?: Partial<ValidatedFilenameError>
): ValidatedFilenameError => ({
  code: ValidatedFilenameErrorCode.INVALID_CHARACTERS,
  ...props,
  __typename: 'ValidatedFilenameError',
});

export const makeMockValidatedFilenameWarning = (
  props?: Partial<ValidatedFilenameWarning>
): ValidatedFilenameWarning => ({
  code: ValidatedFilenameWarningCode.FILENAME_PATTERN_MISMATCH,
  expectedPattern: '^from cedant/report_\\d{8}\\.xlsx$',
  expectedPatterns: ['^from cedant/report_\\d{8}\\.xlsx$'],
  message: 'Filename does not match the expected pattern for this data room.',
  ...props,
  __typename: 'ValidatedFilenameWarning',
});

export const makeMockEventsResponse = (
  props?: Partial<EventsResponse>
): EventsResponse => ({
  events: [makeMockEvent(), makeMockEvent()],
  hasMore: false,
  ...props,
  __typename: 'EventsResponse',
});

export const makeMockEvent = (props?: Partial<Event>): Event => ({
  id: fake.uuid(),
  type: EventType.FILE_CREATED,
  user: makeMockUser(),
  timestamp: recentISODate(),
  path: fake.filePath(),
  storageObject: makeMockFile(),
  ...props,
  __typename: 'Event',
});

export const makeMockCleanupReport = (
  props?: Partial<CleanupReport>
): CleanupReport => ({
  code: oneOfEnum(CleanupStatus),
  ...props,
  __typename: 'CleanupReport',
});

export const makeMockCedantDetails = (
  props?: Partial<CedantDetails>
): CedantDetails => {
  const cedant = props?.cedant || 'SYMETRA';

  return {
    id: cedant,
    cedant,

    directory: makeMockDirectory(),
    name: cedant.toLowerCase(),
    fileById: makeMockFile(),
    events: makeMockEventsResponse(),
    downloadFile: fake.url(),
    cleanupDirectory: makeMockCleanupReport(),
    canCreate: true,
    ...props,
    __typename: 'CedantDetails',
  };
};

export const makeMockTreatyUser = (props?: Partial<TreatyUser>): TreatyUser => {
  return {
    id: fake.uuid(),
    createdAt: fake.pastDate().toISOString(),
    email: fake.email(),
    emailNotifications: ['SYMETRA', 'TOLKIEN', 'ALLIANZSUISSE'],
    lastLogin: fake.recentDate().toISOString(),
    loginCount: fake.number(),
    name: fake.fullName(),
    roles: ['resre_admin', 'resre_user', 'allianzsuisse_user'],
    tenant: '',
    type: oneOfEnum(UserType),
    canBlock: true,
    isBlocked: false,
    hasMFAEnabled: true,
    ...props,
    __typename: 'TreatyUser',
  };
};

export const makeMockTreatyUsers = (users: TreatyUser[]): TreatyUsers => {
  return {
    users,
    count: users.length,
    __typename: 'TreatyUsers',
  };
};

export const makeMockRole = (props?: Partial<Role>): Role => {
  return {
    id: 'resre_user',
    rawPermissions: '[{"allow":["file.*","pii.justify"]}]',
    ...props,
    __typename: 'Role',
  };
};

export const makeMockDataRoomCategory = (
  cedants: CedantDetails[],
  props?: Partial<DataRoomCategory>
): DataRoomCategory => {
  return {
    __typename: 'DataRoomCategory',
    id: fake.uuid(),
    name: 'Test',
    dataRooms: {
      __typename: 'DataRooms',
      objects: cedants,
      count: cedants.length,
    },
    ...props,
  };
};

export const makeMockProblematicFilesObject = (
  props?: Partial<ProblematicFilesObject>
): ProblematicFilesObject => {
  return {
    allowedDataRooms: [
      makeMockCedantDetails({ cedant: 'TOLKIEN' }),
      makeMockCedantDetails({ cedant: 'ABIGAIL' }),
      makeMockCedantDetails({ cedant: 'ALLIANZSUISSE' }),
      makeMockCedantDetails({ cedant: 'BLACKSTONE' }),
      makeMockCedantDetails({ cedant: 'CLEARWATER' }),
    ],
    problematicFiles: [
      makeMockProblematicFiles({ problemType: 'PII Awaiting Justification' }),
      makeMockProblematicFiles({ problemType: 'PII Awaiting Approval' }),
      makeMockProblematicFiles({ problemType: 'AV Processing' }),
      makeMockProblematicFiles({ problemType: 'AV Error' }),
      makeMockProblematicFiles({ problemType: 'ETL Error' }),
      makeMockProblematicFiles({ problemType: 'Error PreProcessing' }),
      makeMockProblematicFiles({
        problemType: 'Processing',
        files: [],
        count: 0,
      }),
      makeMockProblematicFiles(),
    ],
    ...props,
    __typename: 'ProblematicFilesObject',
  };
};

export const makeMockProblematicFiles = (
  props?: Partial<ProblematicFiles>
): ProblematicFiles => {
  return {
    files: [
      makeMockFile(),
      makeMockFile(),
      makeMockFile(),
      makeMockFile(),
      makeMockFile(),
    ],
    problemType: 'Awaiting Upload',
    problemDescription: 'Files waiting for upload.',
    count: 5,
    ...props,
    __typename: 'ProblematicFiles',
  };
};
